/**
 * Encode a resource URI to a URL-safe Base64 string for use in paths
 * 
 * Uses Base64url encoding (RFC 4648 §5):
 * - Replaces + with -
 * - Replaces / with _
 * - Removes padding =
 * 
 * Handles Unicode characters by encoding to UTF-8 first.
 * 
 * @param uri - The full resource URI to encode
 * @returns URL-safe Base64 encoded string
 * 
 * @example
 * const uri = 'https://api.eventer.app/v1/events/abc123';
 * const encoded = encodeResourceUri(uri);
 * // 'aHR0cHM6Ly9hcGkuZXZlbnRlci5hcHAvdjEvZXZlbnRzL2FiYzEyMw'
 */
export function encodeResourceUri(uri: string): string {
    // Encode to UTF-8 then Base64 (handles Unicode)
    const utf8Bytes = new TextEncoder().encode(uri);
    const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join('');
    const base64 = btoa(binaryString);
    
    // Convert to URL-safe Base64url
    return base64
        .replace(/\+/g, '-')  // Plus to minus
        .replace(/\//g, '_')  // Slash to underscore
        .replace(/=/g, '');   // Remove padding
}

/**
 * Decode a URL-safe Base64 string back to the original resource URI
 * 
 * Handles Unicode characters by decoding from UTF-8.
 * 
 * @param encoded - The Base64url encoded string
 * @returns The original resource URI
 * 
 * @example
 * const encoded = 'aHR0cHM6Ly9hcGkuZXZlbnRlci5hcHAvdjEvZXZlbnRzL2FiYzEyMw';
 * const uri = decodeResourceUri(encoded);
 * // 'https://api.eventer.app/v1/events/abc123'
 */
export function decodeResourceUri(encoded: string): string {
    // Restore standard Base64 format
    let base64 = encoded
        .replace(/-/g, '+')  // Minus to plus
        .replace(/_/g, '/'); // Underscore to slash
    
    // Restore padding
    while (base64.length % 4) {
        base64 += '=';
    }
    
    // Decode from Base64 to binary string
    const binaryString = atob(base64);
    
    // Convert binary string to UTF-8 bytes then decode
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
}
